<template>
  <v-dialog v-model="dialog" max-width="600" scrollable>
    <template v-slot:activator="{ props: activatorProps }">
      <v-text-field
        v-bind="activatorProps"
        :model-value="displayText"
        label="Match Templates"
        readonly
        hint="Select which templates to match"
        persistent-hint
        append-inner-icon="mdi-chevron-down"
      >
        <template #prepend-inner>
          <v-chip
            v-if="isAllSelected"
            size="small"
            color="primary"
            closable
            @click:close="clearAll"
          >
            All
          </v-chip>
          <v-chip
            v-else-if="selectedTemplateIds.length > 0"
            size="small"
            color="primary"
          >
            {{ selectedTemplateIds.length }} selected
          </v-chip>
        </template>
      </v-text-field>
    </template>

    <v-card>
      <v-card-title class="d-flex align-center">
        <span>Select Templates</span>
        <v-spacer></v-spacer>
        <v-btn
          icon
          variant="text"
          size="small"
          @click="selectAll"
          title="Select All"
        >
          <v-icon>mdi-checkbox-multiple-marked</v-icon>
        </v-btn>
        <v-btn
          icon
          variant="text"
          size="small"
          @click="clearAll"
          title="Clear All"
        >
          <v-icon>mdi-checkbox-multiple-blank-outline</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text style="max-height: 400px;">
        <v-progress-circular
          v-if="loading"
          indeterminate
          color="primary"
          class="d-flex mx-auto my-4"
        ></v-progress-circular>

        <v-alert v-else-if="filteredTemplates.length === 0" type="info" variant="tonal">
          No templates with existing items found in this project.
        </v-alert>

        <v-list v-else density="compact">
          <template v-for="category in hierarchicalData" :key="category.id">
            <!-- Category Level -->
            <v-list-group :value="category.id">
              <template v-slot:activator="{ props }">
                <v-list-item v-bind="props" class="px-0">
                  <template #prepend>
                    <v-checkbox
                      :model-value="isCategorySelected(category)"
                      :indeterminate="isCategoryIndeterminate(category)"
                      @click.stop
                      @update:model-value="toggleCategory(category)"
                      hide-details
                      density="compact"
                      color="primary"
                    ></v-checkbox>
                    <v-icon v-bind="getIconClass(category.icon)" class="ml-2"></v-icon>
                  </template>
                  <v-list-item-title>{{ category.title }}</v-list-item-title>
                </v-list-item>
              </template>

              <!-- Subcategory Level -->
              <template v-for="subcategory in category.subcategories" :key="subcategory.id">
                <v-list-group
                  v-if="getTemplatesForSubcategory(category.id, subcategory.id).length > 0"
                  :value="subcategory.id"
                  sub-group
                >
                  <template v-slot:activator="{ props }">
                    <v-list-item v-bind="props" class="pl-4">
                      <template #prepend>
                        <v-checkbox
                          :model-value="isSubcategorySelected(category.id, subcategory)"
                          :indeterminate="isSubcategoryIndeterminate(category.id, subcategory)"
                          @click.stop
                          @update:model-value="toggleSubcategory(category.id, subcategory)"
                          hide-details
                          density="compact"
                          color="primary"
                        ></v-checkbox>
                        <v-icon v-if="subcategory.icon" v-bind="getIconClass(subcategory.icon)" class="ml-2"></v-icon>
                      </template>
                      <v-list-item-title>{{ subcategory.title }}</v-list-item-title>
                    </v-list-item>
                  </template>

                  <!-- Template Level -->
                  <v-list-item
                    v-for="template in getTemplatesForSubcategory(category.id, subcategory.id)"
                    :key="template.id"
                    class="pl-12"
                  >
                    <template #prepend>
                      <v-checkbox
                        :model-value="selectedTemplateIds.includes(template.id)"
                        @click.stop
                        @update:model-value="toggleTemplate(template.id)"
                        hide-details
                        density="compact"
                        color="primary"
                      ></v-checkbox>
                    </template>
                    <v-list-item-title>{{ template.title }}</v-list-item-title>
                    <v-list-item-subtitle>{{ template.type }}</v-list-item-subtitle>
                  </v-list-item>
                </v-list-group>
              </template>
            </v-list-group>
          </template>
        </v-list>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="dialog = false">Cancel</v-btn>
        <v-btn color="primary" @click="confirm">Confirm</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useAppStore } from '@/stores/app';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => []
  },
  templates: {
    type: Array,
    default: () => []
  },
  processes: {
    type: Array,
    default: () => []
  },
  issues: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['update:modelValue']);

const appStore = useAppStore();
const dialog = ref(false);
const loading = ref(false);
const categories = ref([]);
const selectedTemplateIds = ref([...props.modelValue]);

// Build sets of template codes that have existing items
const existingTemplateCodes = computed(() => {
  const processTemplateCodes = new Set(
    props.processes.map(p => p.processTemplateCode).filter(Boolean)
  );
  const issueTemplateCodes = new Set(
    props.issues.map(i => i.issueTemplateCode).filter(Boolean)
  );
  return { processTemplateCodes, issueTemplateCodes };
});

// Filter templates to only show those with existing items
const filteredTemplates = computed(() => {
  const { processTemplateCodes, issueTemplateCodes } = existingTemplateCodes.value;

  return props.templates.filter(t => {
    const code = t.code;
    // Handle different type naming conventions
    const type = t.type?.toLowerCase() || '';
    const isProcessTemplate = type.includes('form') || type.includes('process');
    const isIssueTemplate = type.includes('issue');

    if (isProcessTemplate) {
      return processTemplateCodes.has(code);
    } else if (isIssueTemplate) {
      return issueTemplateCodes.has(code);
    }
    return false;
  });
});

// Computed: hierarchical data structure
const hierarchicalData = computed(() => {
  return categories.value.filter(cat => {
    // Only show categories that have subcategories with templates
    return cat.subcategories?.some(sub =>
      filteredTemplates.value.some(t => t.categoryId === cat.id && t.subcategoryId === sub.id)
    );
  });
});

// Computed: all template IDs (only filtered ones)
const allTemplateIds = computed(() => filteredTemplates.value.map(t => t.id));

// Computed: check if all templates are selected
const isAllSelected = computed(() => {
  if (allTemplateIds.value.length === 0) return false;
  return allTemplateIds.value.every(id => selectedTemplateIds.value.includes(id));
});

// Computed: display text for the input field
const displayText = computed(() => {
  if (isAllSelected.value) return 'All Templates';
  if (selectedTemplateIds.value.length === 0) return '';
  return `${selectedTemplateIds.value.length} templates selected`;
});

// Get templates for a specific subcategory
const getTemplatesForSubcategory = (categoryId, subcategoryId) => {
  return filteredTemplates.value.filter(t =>
    t.categoryId === categoryId && t.subcategoryId === subcategoryId
  );
};

// Check if all templates in a category are selected
const isCategorySelected = (category) => {
  const categoryTemplates = filteredTemplates.value.filter(t => t.categoryId === category.id);
  if (categoryTemplates.length === 0) return false;
  return categoryTemplates.every(t => selectedTemplateIds.value.includes(t.id));
};

// Check if category is in indeterminate state
const isCategoryIndeterminate = (category) => {
  const categoryTemplates = filteredTemplates.value.filter(t => t.categoryId === category.id);
  if (categoryTemplates.length === 0) return false;
  const selectedCount = categoryTemplates.filter(t => selectedTemplateIds.value.includes(t.id)).length;
  return selectedCount > 0 && selectedCount < categoryTemplates.length;
};

// Check if all templates in a subcategory are selected
const isSubcategorySelected = (categoryId, subcategory) => {
  const subcategoryTemplates = getTemplatesForSubcategory(categoryId, subcategory.id);
  if (subcategoryTemplates.length === 0) return false;
  return subcategoryTemplates.every(t => selectedTemplateIds.value.includes(t.id));
};

// Check if subcategory is in indeterminate state
const isSubcategoryIndeterminate = (categoryId, subcategory) => {
  const subcategoryTemplates = getTemplatesForSubcategory(categoryId, subcategory.id);
  if (subcategoryTemplates.length === 0) return false;
  const selectedCount = subcategoryTemplates.filter(t => selectedTemplateIds.value.includes(t.id)).length;
  return selectedCount > 0 && selectedCount < subcategoryTemplates.length;
};

// Toggle all templates in a category
const toggleCategory = (category) => {
  const categoryTemplates = filteredTemplates.value.filter(t => t.categoryId === category.id);
  const allSelected = isCategorySelected(category);

  if (allSelected) {
    // Deselect all templates in this category
    selectedTemplateIds.value = selectedTemplateIds.value.filter(
      id => !categoryTemplates.some(t => t.id === id)
    );
  } else {
    // Select all templates in this category
    const newIds = categoryTemplates.map(t => t.id).filter(
      id => !selectedTemplateIds.value.includes(id)
    );
    selectedTemplateIds.value = [...selectedTemplateIds.value, ...newIds];
  }
};

// Toggle all templates in a subcategory
const toggleSubcategory = (categoryId, subcategory) => {
  const subcategoryTemplates = getTemplatesForSubcategory(categoryId, subcategory.id);
  const allSelected = isSubcategorySelected(categoryId, subcategory);

  if (allSelected) {
    // Deselect all templates in this subcategory
    selectedTemplateIds.value = selectedTemplateIds.value.filter(
      id => !subcategoryTemplates.some(t => t.id === id)
    );
  } else {
    // Select all templates in this subcategory
    const newIds = subcategoryTemplates.map(t => t.id).filter(
      id => !selectedTemplateIds.value.includes(id)
    );
    selectedTemplateIds.value = [...selectedTemplateIds.value, ...newIds];
  }
};

// Toggle individual template
const toggleTemplate = (templateId) => {
  const index = selectedTemplateIds.value.indexOf(templateId);
  if (index > -1) {
    selectedTemplateIds.value.splice(index, 1);
  } else {
    selectedTemplateIds.value.push(templateId);
  }
};

// Select all templates
const selectAll = () => {
  selectedTemplateIds.value = [...allTemplateIds.value];
};

// Clear all selections
const clearAll = () => {
  selectedTemplateIds.value = [];
};

// Confirm selection
const confirm = () => {
  emit('update:modelValue', [...selectedTemplateIds.value]);
  dialog.value = false;
};

// Icon helper function
const getIconClass = (icon) => {
  if (!icon) return { icon: 'mdi-folder' };
  if (icon.startsWith('mdi-')) return { icon: icon };
  if (icon.startsWith('fa-') || icon.startsWith('fas fa-') || icon.startsWith('fab fa-')) {
    return { icon: 'fa:' + icon };
  }
  return { icon: `md:${icon}` };
};

// Fetch categories from API
const fetchCategories = async () => {
  loading.value = true;
  try {
    const categoriesResponse = await appStore.apiClient.get('/api/categories');
    categories.value = categoriesResponse || [];

    // If no selection yet and we have filtered templates, select all by default
    if (selectedTemplateIds.value.length === 0 && filteredTemplates.value.length > 0) {
      selectAll();
    }
  } catch (error) {
    console.error('Error fetching categories:', error);
  } finally {
    loading.value = false;
  }
};

// Watch for dialog open to fetch categories
watch(dialog, (newVal) => {
  if (newVal && categories.value.length === 0) {
    fetchCategories();
  }
});

// Watch for prop changes
watch(() => props.modelValue, (newVal) => {
  selectedTemplateIds.value = [...newVal];
}, { deep: true });

// Auto-select all when filtered templates become available
watch(filteredTemplates, (newVal) => {
  if (selectedTemplateIds.value.length === 0 && newVal.length > 0) {
    selectAll();
  }
}, { immediate: true });
</script>
